import { askFormFieldsFilledElsewhere } from '@troia-v3/types';

/**
 * Variáveis gravadas por nós ANTERIORES (`config.saveAs`) que um nó pode usar
 * como valor — hoje, as respostas dos nós "Pergunta" e "Perguntar
 * formulário" e o "Número sequencial", oferecidos no mapeamento do "Salvar
 * formulário" (2026-09-28).
 *
 * Só os antecessores contam: nó que roda depois ainda não gravou nada quando
 * este nó roda.
 */
export interface SavedVariable {
  /** Nome em `variables` (o `saveAs` do nó). */
  name: string;
  /** Nome do nó que grava — é o que a tela mostra. */
  nodeLabel: string;
  nodeType: string;
  /** Template pronto para usar como valor (ex.: `{{variables.nota.answer}}`). */
  template: string;
  /**
   * "Perguntar formulário": o formulário perguntado. Cada resposta fica em
   * `{{variables.<name>.answers.<id do campo>}}` — `formAnswerTemplate`.
   */
  formId?: string;
  /** "Perguntar formulário": perguntas que ele NÃO faz — não têm resposta nele. */
  skipFieldIds?: string[];
}

/** Resposta de UMA pergunta de um "Perguntar formulário" anterior. */
export function formAnswerTemplate(variable: SavedVariable, fieldId: string): string {
  return `{{variables.${variable.name}.answers.${fieldId}}}`;
}

interface NodeLike {
  id: string;
  data?: { type?: string; label?: string; config?: Record<string, unknown> } | null;
}

interface EdgeLike {
  source: string;
  target: string;
}

/** Campo do resultado que vale como "a resposta" de cada tipo de nó. */
const CAMPO_DO_VALOR: Record<string, string> = {
  action_ask_question: 'answer',
  action_ask_form: 'answers',
  action_next_number: 'formatted',
};

export function savedVariablesBefore(nodes: readonly NodeLike[], edges: readonly EdgeLike[], nodeId: string): SavedVariable[] {
  const entradas = new Map<string, string[]>();
  for (const edge of edges) {
    const lista = entradas.get(edge.target);
    if (lista === undefined) entradas.set(edge.target, [edge.source]);
    else lista.push(edge.source);
  }

  const antecessores = new Set<string>();
  const pilha = [...(entradas.get(nodeId) ?? [])];
  while (pilha.length > 0) {
    const atual = pilha.pop() as string;
    if (antecessores.has(atual) || atual === nodeId) continue;
    antecessores.add(atual);
    pilha.push(...(entradas.get(atual) ?? []));
  }

  const vistos = new Set<string>();
  const resultado: SavedVariable[] = [];
  for (const node of nodes) {
    if (!antecessores.has(node.id)) continue;
    const nodeType = node.data?.type ?? '';
    const campo = CAMPO_DO_VALOR[nodeType];
    const saveAs = node.data?.config?.['saveAs'];
    if (campo === undefined || typeof saveAs !== 'string' || saveAs.trim() === '' || vistos.has(saveAs)) continue;
    vistos.add(saveAs);
    const formId = node.data?.config?.['formId'];
    const skip = node.data?.config?.['skipFieldIds'];
    const skipFieldIds = Array.isArray(skip) ? skip.filter((id): id is string => typeof id === 'string') : [];
    resultado.push({
      name: saveAs,
      nodeLabel: node.data?.label?.trim() ? (node.data.label as string).trim() : saveAs,
      nodeType,
      template: `{{variables.${saveAs}.${campo}}}`,
      ...(nodeType === 'action_ask_form' && typeof formId === 'string' && formId !== '' ? { formId } : {}),
      ...(nodeType === 'action_ask_form' && skipFieldIds.length > 0 ? { skipFieldIds } : {}),
    });
  }
  return resultado;
}

/**
 * Perguntas de um "Perguntar formulário" que um "Salvar formulário" DEPOIS
 * dele preenche com outro valor (ex.: o número da sorte, que vem do "Número
 * sequencial") — o motor não as faz (`askFormFieldsFilledElsewhere`, a mesma
 * regra). Devolve id do campo → nome do "Salvar formulário". Vazio para outros nós.
 */
export function formFieldsFilledLater(nodes: readonly NodeLike[], edges: readonly EdgeLike[], nodeId: string): Record<string, string> {
  const node = nodes.find((n) => n.id === nodeId);
  if (node?.data?.type !== 'action_ask_form') return {};

  const saidas = new Map<string, string[]>();
  for (const edge of edges) {
    const lista = saidas.get(edge.source);
    if (lista === undefined) saidas.set(edge.source, [edge.target]);
    else lista.push(edge.target);
  }
  const depois = new Set<string>();
  const pilha = [...(saidas.get(nodeId) ?? [])];
  while (pilha.length > 0) {
    const atual = pilha.pop() as string;
    if (depois.has(atual) || atual === nodeId) continue;
    depois.add(atual);
    pilha.push(...(saidas.get(atual) ?? []));
  }

  const saves = nodes
    .filter((n) => depois.has(n.id) && n.data?.type === 'action_save_form_response')
    .map((n) => ({
      label: n.data?.label?.trim() ? (n.data.label as string).trim() : 'Salvar formulário',
      config: (n.data?.config ?? {}) as { formId?: string; answers?: Record<string, string> },
    }));
  const cfg = node.data?.config ?? {};
  const formId = typeof cfg['formId'] === 'string' ? cfg['formId'] : undefined;
  const saveAs = typeof cfg['saveAs'] === 'string' ? cfg['saveAs'] : undefined;
  return askFormFieldsFilledElsewhere(
    { ...(formId !== undefined ? { formId } : {}), ...(saveAs !== undefined ? { saveAs } : {}) },
    saves,
  );
}
